import { FC, useEffect, useState } from 'react'
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom'
import {
  Alert as MuiAlert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Checkbox,
  FormControlLabel,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material'
import { ArrowBack } from '@mui/icons-material'
import { gql, useQuery, useMutation } from '@apollo/client'
import { ContentHead } from '../../../../components/common/ContentHead'
import { ConfirmationModal } from '../../../../components/common/ConfirmationModal'
import { Alert } from '../../../../components/common/Alert'
import { CardBox } from '../../../../theme/styleComponents'
import { formatStatus, statusColors } from '../../../../utils/billing'
import { BillStatus, Maybe, PrintJobTrustScope, User } from '../../../../../generated'
import AttorneyDropdown from '../../../../components/common/AttorneyDropdown'
import { format, isValid } from 'date-fns'
import { BatchPdfWidget } from './BatchPdfWidget'
import { OverlayLoader } from '../../../../components/common/OverlayLoader'
import { PaginationWithJump } from '../../../../components/common/PaginationWithJump'

/**
 * Batch groupings that correspond to an actual billing cycle, and can therefore be re-derived from
 * their period. Anything else (a repair batch, or the 'MANUAL' fallback) had its contents set
 * deliberately, so Rescan does not apply.
 */
const REAL_BILLING_CYCLES: string[] = ['MID_OF_MONTH', 'END_OF_MONTH', 'CUSTOM_DATE']

const safeFormat = (val: unknown, fmt: string): string => {
  if (!val) return '-'
  const d = new Date(val as string | number)
  return isValid(d) ? format(d, fmt) : '-'
}

const GET_BATCH = gql`
  query GetBillEmailBatchDetail($input: GetBatchInput!) {
    getBillEmailBatch(input: $input) {
      batch {
        id
        billingCycle
        periodYear
        periodMonth
        status
        createdAt
        updatedAt
        items {
          id
          recipientEmail
          recipientName
          recipientClientNumber
          responsibleAttorney
          status
          sendGridMessageId
          errorMessage
          sentAt
          billViewedAt
          createdAt
          bill {
            id
            status
            issueDate
            attorneyApprovedAt
            departmentApprovedAt
            case {
              id
              caseNumber
              title
              ccEmailBillsTo
              billing {
                skipDeptHeadApproval
                billingType
              }
            }
          }
        }
      }
      pagination {
        page
        limit
        totalCount
        totalPages
      }
      stats {
        total
        pending
        sent
        failed
        willSend
        alreadySent
        awaitingApproval
        notSendable
      }
    }
  }
`

const TRIGGER_BATCH = gql`
  mutation TriggerBillEmailBatch($input: TriggerBatchInput!) {
    triggerBillEmailBatch(input: $input) {
      batch {
        id
        status
      }
    }
  }
`

const RETRY_ITEM = gql`
  mutation RetryBillEmailBatchItem($input: RetryBatchItemInput!) {
    retryBillEmailBatchItem(input: $input) {
      item {
        id
        status
      }
    }
  }
`

const TOGGLE_EXCLUSION = gql`
  mutation ToggleBillEmailBatchItemExclusion($input: ToggleBatchItemExclusionInput!) {
    toggleBillEmailBatchItemExclusion(input: $input) {
      item {
        id
        status
      }
    }
  }
`

const RESEND_ITEM = gql`
  mutation ResendBillEmailBatchItem($input: ResendBatchItemInput!) {
    resendBillEmailBatchItem(input: $input) {
      item {
        id
        status
        sentAt
      }
    }
  }
`

const RESCAN_BATCH = gql`
  mutation RescanBillEmailBatch($input: RescanBatchInput!) {
    rescanBillEmailBatch(input: $input) {
      added
      updated
      removed
    }
  }
`


const BILLING_TYPE_OPTIONS = [
  { value: 'ALL', label: 'All Types' },
  { value: 'CONTINGENCY', label: 'Contingency' },
  { value: 'CONTINGENCY_HYBRID', label: 'Contingency Hybrid' },
  { value: 'FLAT_FEE', label: 'Flat Fee' },
  { value: 'HOURLY', label: 'Hourly' },
  { value: 'HYBRID', label: 'Hybrid' },
  { value: 'PRO_BONO', label: 'Pro Bono' },
  { value: 'CONSULT', label: 'Consult' }
] as const
type BillingTypeFilter = (typeof BILLING_TYPE_OPTIONS)[number]['value']


const itemStatusColor = (status: string): string => {
  switch (status) {
    case 'PENDING':
      return '#ED6C02'
    case 'SENT':
      return '#0288D1'
    case 'DELIVERED':
      return '#13BB00'
    case 'FAILED':
      return '#D32F2F'
    case 'BOUNCED':
      return '#D32F2F'
    case 'EXCLUDED':
      return '#9E9E9E'
    default:
      return '#9E9E9E'
  }
}

/** Matches the print-jobs page, which narrows a batch by Responsible Attorney 1. */
const BATCH_ATTORNEY_ROLE = 'responsibleAttorney1'
const BATCH_PAGE_SIZE = 25

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const BatchEmailDetail: FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const batchId = Number(id)

  // Attorney, Billing Type and Trust money are the filters that also narrow the send — see the note on the Send button below.
  const [attorneyId, setAttorneyId] = useState<string | null>(null)
  const [attorneyName, setAttorneyName] = useState<string>('')
  const [billingTypeFilter, setBillingTypeFilter] = useState<BillingTypeFilter>('ALL')
  // "Email Bills without Trust Money": the print-jobs trust test (the matter's trust balance across its
  // parties is above zero right now), so the two pages agree. Unchecked means every bill.
  const [withoutTrustOnly, setWithoutTrustOnly] = useState(false)
  const trustScope = withoutTrustOnly ? PrintJobTrustScope.WithoutTrust : PrintJobTrustScope.All
  const [page, setPage] = useState(0)
  const [showSendConfirmation, setShowSendConfirmation] = useState(false)
  const [showRescanConfirmation, setShowRescanConfirmation] = useState(false)
  const [resendItem, setResendItem] = useState<any | null>(null)

  // Reset to the first page whenever a filter changes, so we never land on an out-of-range page.
  useEffect(() => {
    setPage(0)
  }, [billingTypeFilter, attorneyId, trustScope])

  const {
    data: currentData,
    previousData,
    loading,
    refetch
  } = useQuery(GET_BATCH, {
    variables: {
      input: {
        batchId,
        billingType: billingTypeFilter === 'ALL' ? null : billingTypeFilter,
        ...(attorneyId ? { attorneyIds: [attorneyId], attorneyRole: BATCH_ATTORNEY_ROLE } : {}),
        ...(trustScope !== PrintJobTrustScope.All ? { trustScope } : {}),
        page: page + 1,
        limit: BATCH_PAGE_SIZE
      }
    },
    fetchPolicy: 'network-only',
    // Also flags the refetch after Send / Exclude / Retry / Rescan as loading, so the overlay covers it.
    notifyOnNetworkStatusChange: true
  })

  // Fall back to the previous page's data while a filter/pagination change is in flight,
  // so the table and filters stay rendered instead of flashing a spinner.
  const data = currentData ?? previousData

  const [triggerBatch, { loading: triggering }] = useMutation(TRIGGER_BATCH, {
    onCompleted: () => {
      Alert.success('Batch triggered. Emails will be sent shortly.')
      setShowSendConfirmation(false)
      refetch()
    },
    onError: err => {
      Alert.error(err.message)
      setShowSendConfirmation(false)
    }
  })

  const [retryItem, { loading: retrying }] = useMutation(RETRY_ITEM, {
    onCompleted: () => {
      Alert.success('Item queued for retry')
      refetch()
    },
    onError: err => Alert.error(err.message)
  })

  const [toggleExclusion, { loading: toggling }] = useMutation(TOGGLE_EXCLUSION, {
    onCompleted: () => refetch(),
    onError: err => Alert.error(err.message)
  })

  const [resendBatchItem, { loading: resending }] = useMutation(RESEND_ITEM, {
    onCompleted: () => {
      Alert.success('Email resent successfully')
      setResendItem(null)
      refetch()
    },
    onError: err => {
      Alert.error(err.message)
      setResendItem(null)
    }
  })

  const [rescanBatch, { loading: rescanning }] = useMutation(RESCAN_BATCH, {
    onCompleted: data => {
      const { added, updated, removed } = data?.rescanBillEmailBatch ?? {}
      const parts: string[] = []
      if (added > 0) parts.push(`${added} new recipient(s) added`)
      if (updated > 0) parts.push(`${updated} email address(es) updated`)
      if (removed > 0) parts.push(`${removed} recipient(s) removed`)
      Alert.success(parts.length ? parts.join(', ') : 'Rescan complete — no changes found')
      setShowRescanConfirmation(false)
      refetch()
    },
    onError: err => {
      Alert.error(err.message)
      setShowRescanConfirmation(false)
    }
  })

  const batch = data?.getBillEmailBatch?.batch
  const billingTypeLabel =
    billingTypeFilter === 'ALL' ? '' : BILLING_TYPE_OPTIONS.find(o => o.value === billingTypeFilter)?.label ?? ''
  const trustScopeLabel = withoutTrustOnly ? 'Without Trust Money' : ''
  const trustScopePhrase = withoutTrustOnly ? 'that hold no trust money' : ''
  // Filters that narrow the send, as sentence fragments describing the matters it covers.
  const sendScopeParts = [
    attorneyId ? `where ${attorneyName || 'the selected attorney'} is Responsible Attorney 1` : '',
    billingTypeLabel ? `billed as ${billingTypeLabel}` : '',
    trustScopePhrase
  ].filter(Boolean)
  const sendScopeText = sendScopeParts.join(' and ')
  // Only batches whose grouping is a real billing cycle can be re-derived from their period.
  const canRescan = REAL_BILLING_CYCLES.includes(batch?.billingCycle ?? '')

  // Only block the whole page on the very first load — subsequent filter/page changes
  // refetch in place without tearing down the filters and table.
  if (loading && !data) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    )
  }

  if (!batch) return null

  // Items come back already filtered, sorted and paginated from the server.
  const filteredItems: any[] = batch.items ?? []

  // Aggregate stats span the whole batch (not just the current page/filter).
  const stats = data?.getBillEmailBatch?.stats
  const pagination = data?.getBillEmailBatch?.pagination
  const totalCount = stats?.total ?? 0
  const pendingCount = stats?.pending ?? 0
  const sentCount = stats?.sent ?? 0
  const failedCount = stats?.failed ?? 0
  // How Send treats the pending items, in the same attorney / billing type / trust scope. Adds up to pendingCount.
  const willSendCount = stats?.willSend ?? 0
  const alreadySentCount = stats?.alreadySent ?? 0
  const awaitingApprovalCount = stats?.awaitingApproval ?? 0
  const notSendableCount = stats?.notSendable ?? 0
  const hasSkippedPending = alreadySentCount + awaitingApprovalCount + notSendableCount > 0


  return (
    <CardBox marginTop={3}>
      {/* A filter or page change refetches in place (the previous data stays rendered), so show the
          standard overlay while it is in flight rather than leaving stale rows looking current. */}
      {loading && <OverlayLoader />}
      <ContentHead
        title={`Batch #${batch.id} — ${MONTH_NAMES[(batch.periodMonth ?? 1) - 1]} ${batch.periodYear} (${
          batch.billingCycle
        })`}
        customButton={() => (
          <Box display="flex" gap={1} alignItems="center">
            <Button startIcon={<ArrowBack />} variant="outlined" onClick={() => navigate('/accounts/batch-emails')}>
              Back
            </Button>
            {/* Rescan re-derives a batch from "every bill in this period on this billing cycle", so it
                only applies to batches grouped under a real cycle. A repair batch's contents were
                chosen deliberately; rescanning it would strip them. The server refuses too. */}
            <Tooltip
              title={
                canRescan ? '' : `Batches grouped under "${batch.billingCycle}" are set deliberately and cannot be rescanned`
              }
            >
              <span>
                <Button
                  variant="outlined"
                  disabled={rescanning || !canRescan}
                  onClick={() => setShowRescanConfirmation(true)}
                >
                  {rescanning ? 'Scanning...' : 'Rescan'}
                </Button>
              </span>
            </Tooltip>
            {/* willSendCount is attorney-, billing-type- and trust-scoped server-side, and is the number of emails
                the send will actually deliver — the same figure as the breakdown below. Send stays enabled
                while only already-emailed items are left, since pressing it marks those as sent. */}
            <Button
              variant="contained"
              disabled={batch.status === 'COMPLETED' || willSendCount + alreadySentCount === 0 || triggering}
              onClick={() => setShowSendConfirmation(true)}
            >
              {`Send ${attorneyId ? `${attorneyName || 'Selected Attorney'}'s ` : ''}${
                billingTypeLabel ? `${billingTypeLabel} ` : ''
              }${trustScopeLabel ? `${trustScopeLabel} ` : ''}(${willSendCount} to Email)`}
            </Button>
          </Box>
        )}
      />

      <MuiAlert severity="info" sx={{ mb: 3 }}>
        Emails will only be sent to clients for bills in <strong>Final Bill</strong>, <strong>Sent To Client</strong>,{' '}
        <strong>Partially Paid</strong> or <strong>Paid</strong> status. Bills still in approval will be included on the
        next send, and bills already emailed from the bill page are not emailed again.
        <br />
        The <strong>Resp. Attorney 1</strong> and <strong>Billing Type</strong> filters and the{' '}
        <strong>Email Bills without Trust Money</strong> option limit both this list and what Send delivers.
      </MuiAlert>
      {sendScopeParts.length > 0 && (
        <MuiAlert severity="warning" sx={{ mb: 3 }}>
          Send is limited to matters
          {attorneyId && (
            <>
              {' '}
              where <strong>{attorneyName || 'the selected attorney'}</strong> is Responsible Attorney 1
            </>
          )}
          {attorneyId && billingTypeLabel && ' and'}
          {billingTypeLabel && (
            <>
              {' '}
              billed as <strong>{billingTypeLabel}</strong>
            </>
          )}
          {(attorneyId || billingTypeLabel) && trustScopePhrase && ' and'}
          {trustScopePhrase && (
            <>
              {' '}
              <strong>{trustScopePhrase}</strong>
            </>
          )}
          . Clear {sendScopeParts.length > 1 ? 'these filters' : 'this filter'} to send the whole batch.
        </MuiAlert>
      )}

      {hasSkippedPending && (
        <MuiAlert severity="warning" sx={{ mb: 3 }}>
          Of the {pendingCount} pending, <strong>{willSendCount}</strong> will be emailed.
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {alreadySentCount > 0 && (
              <li>
                <strong>{alreadySentCount}</strong> were already emailed from the bill page. They won't be emailed
                again; Send marks them as sent, with the date they were emailed.
              </li>
            )}
            {awaitingApprovalCount > 0 && (
              <li>
                <strong>{awaitingApprovalCount}</strong> are pre-bills still awaiting attorney approval. They will be
                emailed by a later send once finalized.
              </li>
            )}
            {notSendableCount > 0 && (
              <li>
                <strong>{notSendableCount}</strong> are written off or in another status that is never emailed.
              </li>
            )}
          </Box>
        </MuiAlert>
      )}

      <Box px={2} pb={3} display="flex" gap={3} alignItems="flex-start" flexWrap="wrap">
        <Box flex={1} sx={{ minWidth: 280 }}>
        {/* Summary stats */}
        <Box display="flex" gap={3} mb={3} flexWrap="wrap">
          <Box>
            <Typography variant="caption" color="text.secondary">
              Batch Status
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {batch.status}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Total
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {totalCount}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Pending
            </Typography>
            <Typography variant="body2" fontWeight={600} color="warning.main">
              {pendingCount}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Sent / Delivered
            </Typography>
            <Typography variant="body2" fontWeight={600} color="success.main">
              {sentCount}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Failed / Bounced
            </Typography>
            <Typography variant="body2" fontWeight={600} color="error.main">
              {failedCount}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Created
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {safeFormat(batch.createdAt, 'MMM d, yyyy h:mm a')}
            </Typography>
          </Box>
        </Box>

        {/* Filters */}
        <Box mb={2} display="flex" gap={2} flexWrap="wrap">
          <AttorneyDropdown
            selectedAttorneyId={attorneyId ?? undefined}
            onChange={(value: Maybe<User>) => {
              setAttorneyId(value?.id ?? null)
              setAttorneyName(`${value?.firstName ?? ''} ${value?.lastName ?? ''}`.trim())
            }}
            label="Resp. Attorney 1"
            size="small"
            sx={{ minWidth: 260 }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Filter by Billing Type</InputLabel>
            <Select
              value={billingTypeFilter}
              label="Filter by Billing Type"
              onChange={e => setBillingTypeFilter(e.target.value as BillingTypeFilter)}
            >
              {BILLING_TYPE_OPTIONS.map(o => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        {/* Trust filter. Same test as the print-jobs page and the "Bills with Trust Money" checkbox on
            the bills list, so the screens always agree on which matters hold trust. */}
        <Box mb={2}>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={withoutTrustOnly}
                onChange={e => setWithoutTrustOnly(e.target.checked)}
              />
            }
            label="Email Bills without Trust Money"
          />
          <Typography variant="caption" color="text.secondary" component="div">
            A matter counts as having trust money when its trust balance is above zero right now — the same rule as
            the <strong>Bills with Trust Money</strong> filter on the Bills page.
          </Typography>
        </Box>
        </Box>
        <BatchPdfWidget batchId={Number(batch.id)} totalBills={totalCount} />
      </Box>

      <TableContainer component={Paper} elevation={0}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Matter</TableCell>
              <TableCell>Bill / Billing Type</TableCell>
              <TableCell>Bill Status</TableCell>
              <TableCell>Bill Created At</TableCell>
              <TableCell>Approved At</TableCell>
              <TableCell>Dept Head Approval</TableCell>
              <TableCell>Recipient</TableCell>
              <TableCell>CC Emails</TableCell>
              <TableCell>Email Status</TableCell>
              <TableCell>Sent At</TableCell>
              <TableCell>Bill Opened At</TableCell>
              <TableCell>Error</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={13} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No items match the selected filter
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredItems.map((item: any) => {
                const billStatus: BillStatus | undefined = item.bill?.status
                return (
                  <TableRow key={item.id} hover sx={{ opacity: item.status === 'EXCLUDED' ? 0.45 : 1 }}>
                    <TableCell>
                      {item.bill?.case ? (
                        <>
                          <Link
                            component={RouterLink}
                            to={`/matters/matter-detail/${item.bill.case.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="body2"
                            fontWeight={500}
                            underline="hover"
                          >
                            #{item.bill.case.caseNumber}
                          </Link>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {item.bill.case.title}
                          </Typography>
                          {/* Responsible Attorney 1, unlabelled — without it there is no way to tell
                              whose bills these are while reviewing a batch. Slightly heavier than
                              the title above it: matter titles run long and wrap, so an identically
                              styled line would read as a continuation of the title rather than a
                              separate fact. */}
                          {item.responsibleAttorney && (
                            <Typography variant="caption" color="text.secondary" display="block" fontWeight={600}>
                              {item.responsibleAttorney}
                            </Typography>
                          )}
                        </>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      {item.bill?.id && (
                        <Link
                          component={RouterLink}
                          to={`/billing/bill-detail/${item.bill.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="body2"
                          fontWeight={500}
                          underline="hover"
                          display="block"
                        >
                          #{item.bill.id}
                        </Link>
                      )}
                      {item.bill?.case?.billing?.billingType ? (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {(item.bill.case.billing.billingType as string)
                            .replace(/_/g, ' ')
                            .toLowerCase()
                            .replace(/\b\w/g, (c: string) => c.toUpperCase())}
                        </Typography>
                      ) : (
                        !item.bill?.id && '-'
                      )}
                    </TableCell>
                    <TableCell>
                      {billStatus ? (
                        <Chip
                          label={formatStatus(billStatus)}
                          size="small"
                          sx={{
                            color: statusColors[billStatus] ?? '#666',
                            border: `1px solid ${(statusColors[billStatus] ?? '#666') + '50'}`,
                            backgroundColor: 'transparent',
                            fontSize: '0.7rem'
                          }}
                        />
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {safeFormat(item.bill?.issueDate, 'MMM d, yyyy h:mm a')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {item.bill?.attorneyApprovedAt
                          ? safeFormat(item.bill.attorneyApprovedAt, 'MMM d, yyyy h:mm a')
                          : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {item.bill?.case?.billing?.skipDeptHeadApproval ? (
                        <Typography variant="caption" color="text.secondary">
                          N/A
                        </Typography>
                      ) : item.bill?.departmentApprovedAt ? (
                        <Typography variant="caption" color="text.secondary">
                          {safeFormat(item.bill.departmentApprovedAt, 'MMM d, yyyy')}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          Pending
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {/* Client number links to the contact record. Absent for contacts and some
                          migrated records, so the name falls back to plain text rather than a
                          dead link. */}
                      {item.recipientClientNumber ? (
                        <Link
                          component={RouterLink}
                          to={`/contacts/client/${item.recipientClientNumber}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="body2"
                          fontWeight={500}
                          underline="hover"
                          display="block"
                        >
                          {item.recipientName || item.recipientEmail} (#{item.recipientClientNumber})
                        </Link>
                      ) : (
                        <Typography variant="body2">{item.recipientName || item.recipientEmail}</Typography>
                      )}
                      {item.recipientName && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {item.recipientEmail}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 200 }}>
                      {item.bill?.case?.ccEmailBillsTo ? (
                        item.bill.case.ccEmailBillsTo
                          .split(';')
                          .map((e: string) => e.trim())
                          .filter(Boolean)
                          .map((email: string) => (
                            <Typography key={email} variant="caption" display="block" color="text.secondary">
                              {email}
                            </Typography>
                          ))
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={item.status}
                        size="small"
                        sx={{
                          color: itemStatusColor(item.status),
                          border: `1px solid ${itemStatusColor(item.status)}50`,
                          backgroundColor: 'transparent',
                          fontSize: '0.7rem'
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{safeFormat(item.sentAt, 'MMM d, yyyy h:mm a')}</Typography>
                    </TableCell>
                    <TableCell>
                      {item.billViewedAt ? (
                        <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 500 }}>
                          {safeFormat(item.billViewedAt, 'MMM d, yyyy h:mm a')}
                        </Typography>
                      ) : (
                        // Deliberately "Not opened" rather than a bare dash: this column is blank both
                        // for a client who has not looked and for bills emailed before view tracking
                        // existed, and a dash would read as missing data in either case.
                        <Typography variant="body2" color="text.disabled">
                          Not opened
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 200 }}>
                      {item.errorMessage ? (
                        <Typography variant="caption" color="error" sx={{ wordBreak: 'break-word' }}>
                          {item.errorMessage}
                        </Typography>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" gap={1.5} alignItems="center" justifyContent="flex-end">
                        {item.bill?.id && (
                          <Link
                            component={RouterLink}
                            to={`/billing/bill-detail/${item.bill.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="caption"
                            underline="none"
                            sx={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                          >
                            View Bill
                          </Link>
                        )}
                        {(item.status === 'FAILED' || item.status === 'BOUNCED') && (
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={retrying}
                            onClick={() => retryItem({ variables: { input: { itemId: item.id } } })}
                          >
                            Retry
                          </Button>
                        )}
                        {item.status === 'SENT' && !item.errorMessage && (
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={resending}
                            onClick={() => setResendItem(item)}
                          >
                            Resend
                          </Button>
                        )}
                        {item.status === 'EXCLUDED' && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#13BB00',
                              cursor: toggling ? 'not-allowed' : 'pointer',
                              opacity: toggling ? 0.5 : 1,
                              whiteSpace: 'nowrap'
                            }}
                            onClick={() => !toggling && toggleExclusion({ variables: { input: { itemId: item.id } } })}
                          >
                            Re-enable
                          </Typography>
                        )}
                        {['PENDING', 'FAILED', 'BOUNCED'].includes(item.status) && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#D32F2F',
                              cursor: toggling ? 'not-allowed' : 'pointer',
                              opacity: toggling ? 0.5 : 1,
                              whiteSpace: 'nowrap'
                            }}
                            onClick={() => !toggling && toggleExclusion({ variables: { input: { itemId: item.id } } })}
                          >
                            Exclude
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* `page` state is 0-based (API sends page + 1); PaginationWithJump is 1-based. */}
      <PaginationWithJump
        count={pagination?.totalPages ?? 0}
        page={page + 1}
        onChange={newPage => setPage(newPage - 1)}
        disabled={loading}
      />

      <ConfirmationModal
        isOpen={showRescanConfirmation}
        title="Rescan Batch"
        message={`This will check all bills from this billing period for delivery method or email address changes.\n\n• New email recipients will be added as pending items\n• Existing pending items with changed email addresses will be updated in place\n• Already sent items will not be touched\n\nDo you want to proceed?`}
        confirmText="Rescan"
        cancelText="Cancel"
        variant="info"
        isLoading={rescanning}
        onConfirm={() => rescanBatch({ variables: { input: { batchId: batch.id } } })}
        onCancel={() => setShowRescanConfirmation(false)}
      />

      <ConfirmationModal
        isOpen={showSendConfirmation}
        title="Send Batch Emails"
        message={
          (sendScopeText
            ? `This will send emails ONLY for matters ${sendScopeText} — ${willSendCount} email(s).`
            : `This will send ${willSendCount} email(s) in this batch, across every attorney, billing type and trust balance.`) +
          `\n\nBills already emailed from the bill page are marked sent without a second email; bills still in approval are skipped and can be sent later.` +
          `\n\nDo you want to proceed?`
        }
        confirmText="Send Emails"
        cancelText="Cancel"
        variant="danger"
        isLoading={triggering}
        onConfirm={() =>
          triggerBatch({
            variables: {
              input: {
                batchId: batch.id,
                ...(attorneyId ? { attorneyIds: [attorneyId], attorneyRole: BATCH_ATTORNEY_ROLE } : {}),
                ...(billingTypeLabel ? { billingType: billingTypeFilter } : {}),
                ...(trustScope !== PrintJobTrustScope.All ? { trustScope } : {})
              }
            }
          })
        }
        onCancel={() => setShowSendConfirmation(false)}
      />

      <ConfirmationModal
        isOpen={!!resendItem}
        title="Resend Email"
        message={`This will resend the bill email to ${resendItem?.recipientEmail} for matter #${resendItem?.bill?.case?.caseNumber}. The recipient will receive the same email that was sent originally.\n\nDo you want to proceed?`}
        confirmText="Resend"
        cancelText="Cancel"
        variant="danger"
        isLoading={resending}
        onConfirm={() => resendBatchItem({ variables: { input: { itemId: resendItem?.id } } })}
        onCancel={() => setResendItem(null)}
      />
    </CardBox>
  )
}

export default BatchEmailDetail
